import React, { useCallback, useMemo } from "react";
import { Collapse, GridLegacy, IconButton, TableCell, TextField, Tooltip, Typography } from "@mui/material";
import TranslationWrapper, { getTranslation } from "../../components/TranslationWrapper/TranslationWrapper";
import MainNavbar from "../../components/Navbars/MainNavbar";
import { withStyles } from "tss-react/mui";
import vestigingStyle from "../../assets/jss/material-dashboard-pro-react/views/vestigingStyle";
import JamezzTableV2, { useReloadState } from "../../components/JamezzTable/V2/JamezzTableV2";
import FormElement from "../../components/JamezzTable/FormElement";
import { Link } from "react-router";
import Edit from "@mui/icons-material/Edit";
import { useDialog } from "../../licensing/utils/DialogProvider";
import useJTFilter from "../../components/JamezzTable/V2/useJTFilter";
import { Add, Delete, FreeBreakfast, Lock, Refresh, RestoreFromTrash } from "@mui/icons-material";
import VestigingenFilter, { getVestigingFilterState } from "../../components/VestigingFilter/VestigingFilter";
import { connect } from "react-redux";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import Button from "../../components/CustomButtons/Button";
import axios from "axios";
import { toast } from "react-toastify";
import AreYouSureDialog from "../../components/Dialogs/AreYouSureDialog";
import { _delete, _post } from "../../http";
import { unwrap } from "../../unwrap";

const endPoint = "core/optionGroups";

function OptionGroups(props) {
  const { classes } = props;

  const { reloadState, updateReloadState } = useReloadState();
  const [textField, searchFilter] = useJTFilter("name");

  const [openDialog] = useDialog();
  const renderHeaderCells = useCallback(
    () => [
      { id: "id", label: "#", numeric: true, disablePadding: false, sortable: true },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"OptionGroup name"} tKey={"optionGroups.name"} />,
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
    ],
    []
  );

  const renderRowCells = useCallback((row, isEditable) => {
    return (
      <>
        <TableCell align={"right"} style={{ width: 100 }}>
          {row.id} ({row.apiId})
        </TableCell>
        <TableCell>
          {isEditable ? (
            <FormElement endPoint={endPoint} name={"name"} row={row}>
              <TextField size={"small"} variant={"standard"} />
            </FormElement>
          ) : null}
          {!isEditable ? row.name : null}
        </TableCell>
      </>
    );
  }, []);

  const renderRowActions = useCallback((row, tableOptions) => {
    return (
      <TableCell align={"right"}>
        <Link to={`./${row.id}/edit`}>
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            placement={"bottom"}
            title={<TranslationWrapper tDefault={"Edit"} tKey={"global.jamezzTable.edit"} />}
          >
            <IconButton color={"primary"} simple size={"small"}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Link>

        <AuthenticationWrapper permissionKey={"optionGroups.delete"}>
          {row.is_archivable && tableOptions.showArchive ? (
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"Herstel"} tKey={"global.jamezzTable.restore"} />}
            >
              <Button
                className={classes.actionButton}
                color={"success"}
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper
                            tDefault={"Restore? Are you sure you want to restore this item?"}
                            tKey={"global.jamezztable.confirm.restore.product"}
                          />
                        }
                        onOk={() => {
                          _post(`/core/optionGroups/${row.id}/restore`).finally(updateReloadState);
                        }}
                      />
                    ),
                  });
                }}
                simple
              >
                <RestoreFromTrash className={classes.icon} />
              </Button>
            </Tooltip>
          ) : (
            <></>
          )}
          {row.is_archivable && !tableOptions.showArchive ? (
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement={"bottom"}
              title={<TranslationWrapper tDefault={"Verwijderen"} tKey={"global.jamezzTable.remove"} />}
            >
              <Button
                className={classes.actionButton}
                color={"danger"}
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper
                            tDefault={"Delete? Are you sure you want to delete this item?"}
                            tKey={"global.jamezztable.confirm.delete.product"}
                          />
                        }
                        onOk={() => {
                          _delete("/core/optionGroups/" + row.id).finally(updateReloadState);
                        }}
                      />
                    ),
                  });
                }}
                simple
              >
                <Delete className={classes.icon} />
              </Button>
            </Tooltip>
          ) : (
            <></>
          )}
        </AuthenticationWrapper>
      </TableCell>
    );
  }, []);

  const renderSelectionActions = useCallback((selected, tableOptions) => {
    return (
      <AuthenticationWrapper permissionKey={"optionGroups.multidelete"}>
        <>
          <Tooltip title={<TranslationWrapper tKey={"Delete"} />}>
            <Collapse in={!tableOptions.showArchive}>
              <IconButton
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper tKey={"Are you sure you want to delete the selected option groups?"} />
                        }
                        onOk={() => {
                          _post("/core/optionGroups/multipledelete", { ids: selected }).then((response) => {
                            if (selected.length !== response?.data?.data?.deleted) {
                              toast.error(
                                getTranslation(
                                  "option_groups.archive_incomplete",
                                  "Not all selected items were archivable."
                                )
                              );
                            }

                            updateReloadState();
                          });
                        }}
                      />
                    ),
                  });
                }}
                style={{ color: "lightcoral" }}
              >
                <Delete />
              </IconButton>
            </Collapse>
          </Tooltip>
          <Tooltip title={<TranslationWrapper tKey={"Restore"} />}>
            <Collapse in={tableOptions.showArchive}>
              <IconButton
                onClick={() => {
                  openDialog({
                    children: (
                      <AreYouSureDialog
                        content={
                          <TranslationWrapper tKey={"Are you sure you want to restore the selected option groups?"} />
                        }
                        onOk={() => {
                          _post("/core/optionGroups/multipleRestore", { ids: selected }).then(() => {
                            updateReloadState();
                          });
                        }}
                      />
                    ),
                  });
                }}
                style={{ color: "#66bb6a" }}
              >
                <RestoreFromTrash />
              </IconButton>
            </Collapse>
          </Tooltip>
        </>
      </AuthenticationWrapper>
    );
  }, []);

  const filters = useMemo(() => {
    let filters = {};
    if (searchFilter) {
      filters = searchFilter;
    }
    if (props.filters) {
      filters = { ...filters, ...props.filters };
    }
    return filters;
  }, [searchFilter, props.filters]);
  const forceRetryTranslations = () => {
    const confirmed = confirm(
      getTranslation(
        "categories.retry.confirm",
        "Are you sure you want to force retry all option group translations? This will overwrite existing translations!"
      )
    );

    if (confirmed) {
      axios
        .post("core/optionGroups/forceRetryTranslations", {
          sales_area_id: props.filters.vestiging_id?.value,
        })
        .then((response) => {
          if (response.data) {
            toast.success(getTranslation("producten.resize.queued", "Translations queued, this may take a while"));
          } else {
            toast.error(getTranslation("producten.resize.failed", "Translations failed!"));
          }
        })
        .catch((err) => {
          toast.error(getTranslation("producten.resize.failed", "Translations failed!"));
        });
    }
  };

  const salesarea = unwrap(props.vestiging_id);
  const appTarget = unwrap(props.appTarget);

  return (
    <div>
      <MainNavbar paginaTitel={<TranslationWrapper tDefault={"Option groups"} tKey={"optionGroups.table.title"} />} />

      {salesarea ? (
        <GridContainer>
          <GridItem xs={12}>
            {appTarget === "appv5.js" ? (
              <AuthenticationWrapper
                child={
                  <Link to={"./create"}>
                    <Button className={classes.marginRight} color={"success"}>
                      <Add className={classes.icons} />{" "}
                      <TranslationWrapper
                        tDefault={"Create new option group"}
                        tKey={"optionGroups.createNewOptionGroup"}
                      />
                    </Button>
                  </Link>
                }
                permissionKey={"optionGroups.create"}
              />
            ) : (
              <Typography>
                <Lock />
                <TranslationWrapper tKey={"Only supported in appv5"} />
              </Typography>
            )}
          </GridItem>
          <AuthenticationWrapper permissionKey={"producten.translate.force"}>
            <Button className={classes.marginRight} color={"danger"} onClick={forceRetryTranslations}>
              <Refresh className={classes.icons} />
              <TranslationWrapper tDefault={"Force retry translations"} tKey={"producten.translate.force"} />
            </Button>
          </AuthenticationWrapper>
        </GridContainer>
      ) : null}
      <VestigingenFilter />
      {textField}
      <GridLegacy container spacing={1}>
        <GridLegacy item xs={12}>
          <JamezzTableV2
            checkboxSelection={true}
            delete={"option_groups.delete"}
            dense={true}
            endPoint={endPoint}
            filters={filters}
            headerIcon={<FreeBreakfast />}
            reloadState={reloadState}
            renderCellsForRow={renderRowCells}
            renderHeaderCells={renderHeaderCells}
            renderRowActions={renderRowActions}
            renderSelectionActions={renderSelectionActions}
            tableTitle={<TranslationWrapper tDefault={"Option groups"} tKey={"optionGroups.table.title"} />}
            uuid={"OptionGroups-diu1j209jd89jsx"}
          />
        </GridLegacy>
      </GridLegacy>
    </div>
  );
}

const mapStateToProps = (state) => {
  let filters = getVestigingFilterState(state, true);

  return {
    filters,
    vestiging_id: state.filtersV2.vestiging_id?.value,
    appTarget: state.filtersV2.vestiging_id?.appTarget,
  };
};

export default connect(mapStateToProps)(withStyles(OptionGroups, vestigingStyle));
